import { type MetricType } from 'features/charts-v2/types'

export const X_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH = 120
export const Y_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH = 120

// Horizontal bar Y-axis: match V1's 30-char cap so the label column
// sizes itself from content rather than being fixed at 180/200 px.
export const HORIZONTAL_BAR_Y_AXIS_TRUNCATE_CHARS = 30
export const HORIZONTAL_BAR_Y_AXIS_WRAP_CHARS = 30

// Maximum number of y-axis category labels to show on horizontal bar charts.
// Beyond this, labels are skipped evenly to avoid overlap.
export const MAX_Y_AXIS_LABELS = 40
export const MAX_X_AXIS_LABELS = 10

// Recharts' own default XAxis height when a chart doesn't pass one explicitly
// (neither HorizontalBar nor StackedBarChart does) — see
// node_modules/recharts/es6/state/selectors/axisSelectors.js's `implicitXAxis.height`.
// Used to compute the actual plot height available for y-axis category rows.
export const DEFAULT_X_AXIS_HEIGHT_PX = 30

export type OverflowMode = 'expand' | 'wrap' | 'truncate'

/**
 * Format numeric axis tick values with thousands separators
 * e.g., 8800 -> "8,800"
 */
export const formatAxisNumber = (value: number): string => {
  return value.toLocaleString()
}

/**
 * Format axis number based on metric type
 * - Sentiment/Rating: up to 2 decimal places, trailing zeros dropped
 * - Other metrics: the actual tick value with locale formatting (thousands separators)
 *
 * The label must name the value the gridline actually sits on. Count metrics
 * previously routed through a nearest-10/100/1000 rounding pass, which
 * relabeled the non-round ticks Recharts emits on explicit domains (e.g. a
 * synced [120, 490] domain yields ticks at 215 and 405, which were labeled
 * "220" and "410") at positions the gridline does not occupy.
 */
export const formatMetricAxisNumber = (
  value: number,
  metricType?: MetricType,
): string => {
  if (metricType === 'sentiment' || metricType === 'rating') {
    return Number(value.toFixed(2)).toLocaleString()
  }
  return value.toLocaleString()
}

/**
 * Get the appropriate axis domain for a metric type
 * - Sentiment: [-1, 1]
 * - Rating: [1, 5]
 * - Others: undefined (auto-calculated from data)
 */
const SENTIMENT_AXIS_DOMAIN: [number, number] = [-1, 1]
const RATING_AXIS_DOMAIN: [number, number] = [1, 5]

export const getMetricAxisDomain = (
  metricType?: MetricType,
): [number, number] | undefined => {
  if (metricType === 'sentiment') return SENTIMENT_AXIS_DOMAIN
  if (metricType === 'rating') return RATING_AXIS_DOMAIN
  return undefined
}

export interface OverflowConfig {
  height: number
  margin: number
  maxCharacters: number | null
}

export const OVERFLOW_CONFIG: Record<OverflowMode, OverflowConfig> = {
  expand: {
    height: 180,
    margin: 20,
    maxCharacters: null,
  },
  wrap: {
    height: 120,
    margin: 30,
    maxCharacters: null,
  },
  truncate: {
    height: 120,
    margin: 20,
    maxCharacters: 20,
  },
}

/**
 * Utility function to wrap text into multiple lines
 */
export const wrapText = (
  text: string,
  maxWidth: number,
  fontSize: number,
): string[] => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return [text]

  ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`

  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const metrics = ctx.measureText(testLine)

    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines.length > 0 ? lines : [text]
}

/**
 * Utility function to truncate text at character limit
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

/**
 * Calculate the maximum text width from an array of labels using Canvas measureText
 * @param labels - Array of label strings to measure
 * @param font - CSS font string matching Recharts' default tick font
 * @returns The maximum width in pixels
 */
export const calculateMaxTextWidth = (
  labels: string[],
  font: string = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
): number => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return 150

  ctx.font = font
  let maxWidth = 0
  for (const label of labels) {
    const width = ctx.measureText(label).width
    if (width > maxWidth) {
      maxWidth = width
    }
  }
  return maxWidth
}

/**
 * Calculate the required X-axis height based on label content for angled (-45 degree) labels
 * @param labels - Array of label strings to measure
 * @param fontSizePx - Font size in pixels
 * @param minHeight - Minimum height to ensure readability (default: 60)
 * @param maxHeight - Maximum height to prevent excessive spacing (default: 200)
 * @returns The calculated height in pixels (clamped between min and max)
 */
export const calculateXAxisHeightFromLabels = (
  labels: string[],
  fontSizePx: number = 13,
  minHeight: number = 60,
  maxHeight: number = 200,
): number => {
  const font = `${fontSizePx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
  const maxWidth = calculateMaxTextWidth(labels, font)

  // For labels rotated at -45 degrees, the vertical space needed is:
  // height = textWidth * sin(45deg) + padding
  // sin(45deg) ~ 0.707
  const calculatedHeight = maxWidth * 0.707 + 20

  return Math.max(minHeight, Math.min(maxHeight, calculatedHeight))
}

/**
 * Right-margin headroom needed so -45°-rotated X-axis labels (anchored at each
 * column's center, growing up-and-to-the-right) aren't clipped at the grid's
 * right edge. Only columns near the right edge actually overhang it, so this is
 * far tighter than reserving the global maximum label width's full projection.
 *
 * @param labels        Column labels, left-to-right
 * @param fontSizePx    Label font size in pixels
 * @param cellWidth     Per-column width — pass the *minimum* cell width for a safe
 *                      upper bound (real cellWidth ≥ min → real overhang ≤ this value)
 * @param safetyFactor  Multiplier on the per-label projection to leave headroom for
 *                      font-measurement slack (canvas vs. SVG) and label thickness;
 *                      default 1 (no extra headroom)
 * @returns Required right margin in px (0 if nothing overhangs the grid edge)
 */
export const calculateRotatedXAxisRightOverhang = (
  labels: string[],
  fontSizePx: number,
  cellWidth: number,
  safetyFactor: number = 1,
): number => {
  const font = `${fontSizePx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const n = labels.length
  if (!ctx || n === 0) return 0
  ctx.font = font
  let maxOverhang = 0
  labels.forEach((label, i) => {
    // Horizontal projection of this label at -45°, with optional safety headroom
    const projected = ctx.measureText(label).width * 0.707 * safetyFactor
    // Right extent of this label relative to the grid's right edge (n * cellWidth)
    const overhang = (i + 0.5) * cellWidth + projected - n * cellWidth
    if (overhang > maxOverhang) maxOverhang = overhang
  })
  return maxOverhang
}

/**
 * Split a label into lines at word boundaries, targeting a given character limit per line.
 * Mirrors V1's splitLabelForChart logic.
 */
export const splitLabelToLines = (
  label: string,
  lineLimit: number,
): string[] => {
  const words = label.split(' ')
  const lines: string[] = []
  let line = ''

  for (const word of words) {
    if (line.length + word.length < lineLimit) {
      line += `${word} `
    } else {
      if (line) lines.push(line.trimEnd())
      line = `${word} `
    }
  }
  if (line) lines.push(line.trimEnd())
  return lines.length > 0 ? lines : [label]
}

/**
 * Calculate the Y-axis width for horizontal bar charts by measuring the actual rendered
 * text after applying the active overflow mode. Mirrors V1 Chart.js auto-sizing behaviour:
 * truncate/wrap first, then measure, so the column is never wider than it needs to be.
 *
 * @param labels - The category label strings from chart data
 * @param overflowMode - The active Y-axis overflow setting
 * @param fontSizePx - The chart font size in pixels
 * @returns Width in pixels, clamped between 100 and 400
 */
export const calculateHorizontalBarYAxisWidth = (
  labels: string[],
  overflowMode: OverflowMode,
  fontSizePx: number,
): number => {
  const font = `${fontSizePx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
  const MIN = 100
  const MAX = 400

  let sample: string[]
  if (overflowMode === 'expand') {
    sample = labels
  } else if (overflowMode === 'truncate') {
    sample = labels.map((l) =>
      truncateText(l, HORIZONTAL_BAR_Y_AXIS_TRUNCATE_CHARS),
    )
  } else {
    // wrap: measure the longest wrapped line, not the full label
    sample = labels.flatMap((l) =>
      splitLabelToLines(l, HORIZONTAL_BAR_Y_AXIS_WRAP_CHARS),
    )
  }

  return Math.max(MIN, Math.min(MAX, calculateMaxTextWidth(sample, font) + 16))
}

/**
 * Trim a single line to fit within maxWidth (px), appending '…' if cut.
 * Uses canvas measureText for accuracy; falls back to a char-estimate when canvas
 * is unavailable (e.g. server-side / test environments without jsdom canvas).
 */
export const ellipsizeToWidth = (
  text: string,
  maxWidth: number,
  fontSize: number,
): string => {
  if (maxWidth <= 0) return '…'

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    // Rough fallback: ~0.55× fontSize per char
    const charBudget = Math.max(1, Math.floor(maxWidth / (fontSize * 0.55)))
    if (text.length <= charBudget) return text
    return text.slice(0, charBudget) + '…'
  }

  ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`

  if (ctx.measureText(text).width <= maxWidth) return text

  // Binary search for the longest prefix whose 'prefix…' fits within maxWidth.
  let lo = 0
  let hi = text.length - 1
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    if (ctx.measureText(text.slice(0, mid) + '…').width <= maxWidth) {
      lo = mid
    } else {
      hi = mid - 1
    }
  }

  return lo > 0 ? text.slice(0, lo) + '…' : '…'
}

/**
 * Wrap text to at most maxLines lines sized to maxWidth (px).
 * If the natural wrap produces more lines than maxLines, the overflow words are
 * collapsed into the last kept line and ellipsized to fit.
 * Each line is individually ellipsized to handle single unbreakable over-wide words.
 */
export const wrapTextToLines = (
  text: string,
  maxWidth: number,
  fontSize: number,
  maxLines: number,
): string[] => {
  if (maxWidth <= 0) return ['…']

  const lines = wrapText(text, maxWidth, fontSize)

  if (lines.length <= maxLines) {
    // Each line still gets ellipsized in case a single long word exceeded the width.
    return lines.map((line) => ellipsizeToWidth(line, maxWidth, fontSize))
  }

  // Too many lines: keep the first maxLines-1 verbatim, collapse the rest into
  // one final ellipsized line.
  const kept = lines.slice(0, maxLines - 1)
  const overflow = lines.slice(maxLines - 1).join(' ')
  return [...kept, ellipsizeToWidth(overflow, maxWidth, fontSize)]
}

/**
 * Calculate the available width for X-axis labels based on chart width and number of data points
 * @param chartWidth - The total width of the chart container
 * @param numDataPoints - The number of data points to display
 * @param margins - Optional margins object with left and right values
 * @returns The calculated maxWidth per label (clamped between min and max bounds)
 */
export const calculateAvailableXAxisWidth = (
  chartWidth: number,
  numDataPoints: number,
  margins: { left?: number; right?: number } = {},
): number => {
  const { left = 40, right = 30 } = margins
  const availableWidth = chartWidth - left - right

  // For angled labels (-45 degrees), we need more space per label
  const baseWidthPerLabel = availableWidth / numDataPoints
  const angledMultiplier = 1.4
  const calculatedWidth = baseWidthPerLabel * angledMultiplier

  return Math.max(80, Math.min(250, calculatedWidth))
}

// Minimum horizontal stride (px) a single X-axis tick label needs so that
// adjacent labels don't overlap. Time-series labels render rotated -45°
// (see CustomXAxisTick), so their horizontal footprint is small; this is the
// spacing at which two rotated short date labels clear each other.
export const MIN_ROTATED_X_LABEL_STRIDE_PX = 36

/**
 * Compute the Recharts `interval` for time-series X-axis tick labels (and the
 * matching datapoint-label thinning).
 *
 * `interval` is Recharts shorthand for "render one label, then skip N": 0 shows
 * every label, 1 shows every other, and so on. The goal is to show every
 * bucket's label whenever the labels physically fit and only thin when they
 * genuinely can't.
 *
 * This previously divided the bucket count by a flat cap of MAX_X_AXIS_LABELS
 * (10), which is width-blind: any series with 11-20 buckets got interval 1
 * (every OTHER label) even on a wide chart with ample room. A weekly breakdown
 * over a few months lands in that band, so weekly charts dropped every other
 * week's label (PROD-4514). Deriving the cap from the available plot width
 * instead lets a wide chart show all its weekly labels while still thinning a
 * genuinely dense axis (e.g. daily over a year). MAX_X_AXIS_LABELS stays as a
 * floor so a narrow chart still shows at least that many.
 *
 * @param bucketCount   Number of time buckets (datapoints) on the X-axis
 * @param chartWidthPx  Plot-area width in px
 * @returns The Recharts `interval` value (0 = show every label)
 */
export const computeXAxisLabelInterval = (
  bucketCount: number,
  chartWidthPx: number,
): number => {
  // Mirror calculateAvailableXAxisWidth's default left/right margins.
  const availableWidth = chartWidthPx - 40 - 30
  const widthCap = Math.floor(availableWidth / MIN_ROTATED_X_LABEL_STRIDE_PX)
  const maxLabels = Math.max(MAX_X_AXIS_LABELS, widthCap)
  return Math.max(0, Math.ceil(bucketCount / maxLabels) - 1)
}
